import { useReducer, useMemo } from 'react';

type JoinUsParams = {
  sortBy?: 'name' | 'email' | 'phone' | 'type' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  page: number;
  limit: number;
  search?: string;
  type?: 'Rider' | 'Partner';
};

const getInitialParams = (): JoinUsParams => {
  return {
    sortBy: 'createdAt',
    sortOrder: 'desc',
    page: 1,
    limit: 10,
    search: undefined,
    type: undefined,
  };
};

type Action =
  | { type: 'SET_SEARCH'; payload: string | undefined }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'SET_LIMIT'; payload: number }
  | { type: 'SET_SORT'; payload: { sortBy: string; sortOrder: 'asc' | 'desc' } }
  | { type: 'SET_TYPE_FILTER'; payload: 'Rider' | 'Partner' | undefined }
  | { type: 'SET_FILTERS'; payload: Partial<JoinUsParams>; resetPage?: boolean }
  | { type: 'RESET' };

function reducer(state: JoinUsParams, action: Action): JoinUsParams {
  switch (action.type) {
    case 'SET_SEARCH':
      return { ...state, search: action.payload, page: 1 };
    case 'SET_PAGE':
      return state.page === action.payload ? state : { ...state, page: action.payload };
    case 'SET_LIMIT':
      return { ...state, limit: action.payload, page: 1 };
    case 'SET_SORT':
      return {
        ...state,
        sortBy: action.payload.sortBy as JoinUsParams['sortBy'],
        sortOrder: action.payload.sortOrder,
      };
    case 'SET_TYPE_FILTER':
      return { ...state, type: action.payload, page: 1 };
    case 'SET_FILTERS':
      return {
        ...state,
        ...Object.fromEntries(
          Object.entries(action.payload).map(([key, val]) => [key, val === '' ? undefined : val])
        ),
        ...(action.resetPage && { page: 1 }),
      };
    case 'RESET':
      return getInitialParams();
    default:
      return state;
  }
}

export function useManageJoinUs() {
  const [params, dispatch] = useReducer(reducer, getInitialParams());

  const stableParams = useMemo(() => params, [
    params.sortBy,
    params.sortOrder,
    params.page,
    params.limit,
    params.search,
    params.type,
  ]);

  return { params: stableParams, dispatch };
}